import { useCallback, useEffect, useMemo, useState } from 'react';
import { fuelingHistoryService, type FuelingHistory } from '../../api/fueling.history';
import FilterOptions from './components/FilterOptions';
import {
  Search,
  Car,
  User,
  Calendar,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  Image as ImageIcon,
  RefreshCw,
  Filter,
  Droplets,
  Banknote,
  Building2,
  Receipt,
  Activity,
  Users,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Button,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR');
};

const formatTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

type HistoryView = 'finalizados' | 'auditoria' | 'todos';

const getHistoryEntryType = (status?: string) => {
  const normalizedStatus = (status || '').toLowerCase();
  return normalizedStatus === 'aprovado' || normalizedStatus === 'rejeitado'
    ? 'Auditoria'
    : 'Finalização';
};

const HistoricoDeAbastecimento = () => {
  const [fuelings, setFuelings] = useState<FuelingHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [view, setView] = useState<HistoryView>('todos');

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [selectedFueling, setSelectedFueling] = useState<FuelingHistory | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); } }, [toast]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allFuelings = await fuelingHistoryService.getAllFuelingHistory();
      setFuelings(allFuelings);
    } catch (fetchError) {
      console.error(fetchError);
      setError('Erro ao carregar o histórico de abastecimento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filterCategories = useMemo(() => {
    const statusOptions = [
      { id: 'finalizado', label: 'Finalizado' },
      { id: 'aprovado', label: 'Aprovado (Auditoria)' },
      { id: 'rejeitado', label: 'Rejeitado (Auditoria)' },
    ];
    const tipoOptions = [
      { id: 'finalizacao', label: 'Apenas finalizações' },
      { id: 'auditoria', label: 'Apenas auditoria' },
    ];
    const vehicleOptions = Array.from(new Set(fuelings.map(f => f.vehicle_id)))
      .map(vId => {
        const f = fuelings.find(x => x.vehicle_id === vId);
        const label = f?.vehicle
          ? `${f.vehicle.brand || 'Veículo'} - ${f.vehicle.license_plate || `#${vId}`}`
          : `Veículo #${vId}`;
        return { id: String(vId), label };
      });
    const providerOptions = Array.from(new Set(fuelings.map(f => f.provider_id)))
      .map(pId => {
        const f = fuelings.find(x => x.provider_id === pId);
        const label = f?.provider?.name || `Fornecedor #${pId}`;
        return { id: String(pId), label };
      });
    return {
      Status: statusOptions,
      Tipo: tipoOptions,
      Veículo: vehicleOptions,
      Fornecedor: providerOptions,
    };
  }, [fuelings]);

  const filteredFuelings = useMemo(() => {
    return fuelings.filter((fueling) => {
      const normalizedStatus = (fueling.status || '').toLowerCase();
      if (view === 'finalizados' && normalizedStatus !== 'finalizado') return false;
      if (view === 'auditoria' && normalizedStatus !== 'aprovado' && normalizedStatus !== 'rejeitado') return false;

      const selectedStatus = appliedFilters.Status || [];
      if (selectedStatus.length > 0 && !selectedStatus.includes(normalizedStatus)) return false;

      const selectedTipo = appliedFilters.Tipo || [];
      if (selectedTipo.length > 0) {
        const isAud = normalizedStatus === 'aprovado' || normalizedStatus === 'rejeitado';
        if (selectedTipo.includes('auditoria') && !isAud) return false;
        if (selectedTipo.includes('finalizacao') && isAud) return false;
      }

      const selectedVehicles = appliedFilters.Veículo || [];
      if (selectedVehicles.length > 0 && !selectedVehicles.includes(String(fueling.vehicle_id))) return false;

      const selectedProviders = appliedFilters.Fornecedor || [];
      if (selectedProviders.length > 0 && !selectedProviders.includes(String(fueling.provider_id))) return false;

      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        String(fueling.id).includes(term) ||
        fueling.fuel_type?.toLowerCase().includes(term) ||
        fueling.status?.toLowerCase().includes(term) ||
        fueling.action?.toLowerCase().includes(term) ||
        fueling.details?.toLowerCase().includes(term) ||
        fueling.invoice_number?.toLowerCase().includes(term) ||
        fueling.notes?.toLowerCase().includes(term) ||
        String(fueling.amount).includes(term) ||
        (fueling.total_cost && String(fueling.total_cost).includes(term)) ||
        fueling.vehicle?.brand?.toLowerCase().includes(term) ||
        fueling.vehicle?.license_plate?.toLowerCase().includes(term) ||
        fueling.employee?.name?.toLowerCase().includes(term) ||
        fueling.employee?.cpf?.toLowerCase().includes(term) ||
        fueling.provider?.name?.toLowerCase().includes(term)
      );
    });
  }, [fuelings, searchTerm, view, appliedFilters]);

  const summary = useMemo(() => {
    const finalizados = fuelings.filter(
      (item) => (item.status || '').toLowerCase() === 'finalizado',
    ).length;
    const auditoria = fuelings.filter((item) => {
      const status = (item.status || '').toLowerCase();
      return status === 'aprovado' || status === 'rejeitado';
    }).length;
    const totalValor = fuelings.reduce((sum, f) => sum + (f.total_cost || 0), 0);
    const totalLitros = fuelings.reduce((sum, f) => sum + (f.amount || 0), 0);
    return {
      total: fuelings.length,
      finalizados,
      auditoria,
      totalValor,
      totalLitros,
    };
  }, [fuelings]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(filteredFuelings.length / ITEMS_PER_PAGE)),
    [filteredFuelings.length],
  );

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const { fuelingsPaginados, pageRange } = useMemo(() => {
    const total = filteredFuelings.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      fuelingsPaginados: filteredFuelings.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredFuelings, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  const renderStatusBadge = (status?: string) => {
    const normalizedStatus = (status || 'finalizado').toLowerCase();
    let classes = 'bg-amber-50 text-amber-800 ring-amber-600/20';
    let label = status || 'finalizado';
    if (normalizedStatus === 'finalizado') { classes = 'bg-slate-100 text-slate-700 ring-slate-600/20'; label = 'Finalizado'; }
    else if (normalizedStatus === 'aprovado') { classes = 'bg-emerald-50 text-emerald-800 ring-emerald-600/20'; label = 'Aprovado'; }
    else if (normalizedStatus === 'rejeitado') { classes = 'bg-rose-50 text-rose-800 ring-rose-600/20'; label = 'Rejeitado'; }
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${classes}`}>
        {label}
      </span>
    );
  };

  const renderTipoBadge = (status?: string) => {
    const isAud = getHistoryEntryType(status) === 'Auditoria';
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${isAud ? 'bg-blue-50 text-blue-800 ring-blue-600/20' : 'bg-slate-50 text-slate-700 ring-slate-600/20'}`}>
        <Activity className="h-3 w-3 mr-1 stroke-[2.2]" />
        {isAud ? 'Auditoria' : 'Finalização'}
      </span>
    );
  };

  const handleViewFueling = (f: FuelingHistory) => {
    setSelectedFueling(f);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedFueling(null);
    setIsViewModalOpen(false);
  };

  const handleImageClick = (url: string) => setSelectedImage(url);
  const handleCloseImageModal = () => setSelectedImage(null);

  return (
    <>
      <PageHeader
        title="Histórico de Abastecimento"
        description="Finalizações e auditoria · Histórico completo de abastecimentos — acompanhe todas as finalizações e trilha de auditoria."
        hideTitleInBody
      />

      {toast && (
        <div className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-5">
        {_error && (
          <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-bold leading-tight">Não foi possível carregar</span>
              <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{_error}</span>
            </div>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50 px-4 py-4 shadow-[0_1px_2px_0_rgba(15,23,42,0.04)] transition-all hover:shadow-[0_4px_16px_-8px_rgba(15,23,42,0.15)] hover:-translate-y-0.5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] font-bold uppercase tracking-wider text-slate-500 leading-none">Total registros</div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-[28px] font-extrabold tracking-tight tabular-nums text-slate-900 leading-none">{summary.total}</span>
                </div>
                <div className="mt-2 text-[11.5px] font-semibold leading-tight text-slate-500">
                  Cadastrados no sistema
                </div>
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700 shadow-inner ring-1 ring-inset ring-slate-200/50">
                <Users className="h-5.5 w-5.5 stroke-[1.9]" />
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 px-4 py-4 shadow-[0_1px_2px_0_rgba(37,99,235,0.06)] transition-all hover:shadow-[0_4px_16px_-8px_rgba(37,99,235,0.25)] hover:-translate-y-0.5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] font-bold uppercase tracking-wider text-blue-600 leading-none">Auditoria</div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-[28px] font-extrabold tracking-tight tabular-nums text-blue-700 leading-none">{summary.auditoria}</span>
                </div>
                <div className="mt-2 text-[11.5px] font-semibold leading-tight text-blue-600/80">
                  Aprovados / Rejeitados
                </div>
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-200 text-blue-700 shadow-inner ring-1 ring-inset ring-blue-200/60">
                <Activity className="h-5.5 w-5.5 stroke-[1.9]" />
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 px-4 py-4 shadow-[0_1px_2px_0_rgba(16,185,129,0.06)] transition-all hover:shadow-[0_4px_16px_-8px_rgba(16,185,129,0.25)] hover:-translate-y-0.5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] font-bold uppercase tracking-wider text-emerald-700 leading-none">Finalizações</div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-[28px] font-extrabold tracking-tight tabular-nums text-emerald-700 leading-none">{summary.finalizados}</span>
                </div>
                <div className="mt-2 text-[11.5px] font-semibold leading-tight text-emerald-700/80">
                  Abastecimentos finalizados
                </div>
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-200 text-emerald-700 shadow-inner ring-1 ring-inset ring-emerald-200/60">
                <Droplets className="h-5.5 w-5.5 stroke-[1.9]" />
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50 px-4 py-4 shadow-[0_1px_2px_0_rgba(168,85,247,0.06)] transition-all hover:shadow-[0_4px_16px_-8px_rgba(168,85,247,0.25)] hover:-translate-y-0.5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] font-bold uppercase tracking-wider text-purple-700 leading-none">Volume total</div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-[26px] font-extrabold tracking-tight tabular-nums text-purple-700 leading-none">{summary.totalLitros.toLocaleString('pt-BR')}</span>
                  <span className="text-[13px] font-bold text-purple-600">L</span>
                </div>
                <div className="mt-2 text-[11.5px] font-semibold leading-tight text-purple-700/80">
                  Litros abastecidos
                </div>
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-100 to-fuchsia-200 text-purple-700 shadow-inner ring-1 ring-inset ring-purple-200/60">
                <Droplets className="h-5.5 w-5.5 stroke-[1.9]" />
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 px-4 py-4 shadow-[0_1px_2px_0_rgba(245,158,11,0.06)] transition-all hover:shadow-[0_4px_16px_-8px_rgba(245,158,11,0.25)] hover:-translate-y-0.5 sm:col-span-2 lg:col-span-1">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] font-bold uppercase tracking-wider text-amber-700 leading-none">Valor total</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-[22px] font-extrabold tracking-tight tabular-nums text-amber-800 leading-none">{formatCurrency(summary.totalValor)}</span>
                </div>
                <div className="mt-2 text-[11.5px] font-semibold leading-tight text-amber-700/80">
                  Gasto com combustível
                </div>
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-200 text-amber-700 shadow-inner ring-1 ring-inset ring-amber-200/60">
                <Banknote className="h-5.5 w-5.5 stroke-[1.9]" />
              </div>
            </div>
          </div>
        </div>

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Todos os abastecimentos</h2>
                <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                  Finalizações e auditoria com filtros avançados
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-2xl">
                <div className="relative flex-1 min-w-0">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    placeholder="Buscar veículo, motorista, fornecedor, NF, valor..."
                    className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pl-10 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 h-10"
                  />
                </div>
                <select
                  value={view}
                  onChange={(event) => { setView(event.target.value as HistoryView); setCurrentPage(1); }}
                  className="inline-flex h-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-[13px] font-semibold text-slate-700 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 hover:bg-slate-50"
                >
                  <option value="todos">📋 Tudo</option>
                  <option value="finalizados">⛽ Finalizações</option>
                  <option value="auditoria">✅ Auditoria</option>
                </select>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setIsFilterOpen(true)}
                  className="h-10 shrink-0"
                >
                  <Filter className="h-4 w-4 stroke-[1.9]" />
                  Filtros
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => { setSubmitting(true); void loadData().finally(() => setSubmitting(false)); }}
                  title="Atualizar informações"
                  className="h-10 shrink-0"
                  disabled={submitting}
                >
                  <RefreshCw className={`h-4 w-4 stroke-[1.9] ${submitting ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && fuelings.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">Carregando histórico...</span>
              </div>
            ) : (
              <ModernTable className="table-fixed layout-fixed">
                <Thead>
                  <Tr>
                    <Th className="w-[11%] min-w-[150px] max-w-[190px]">Data</Th>
                    <Th className="w-[13%] min-w-[180px] max-w-[240px]">Veículo</Th>
                    <Th className="w-[12%] min-w-[170px] max-w-[220px]">Motorista</Th>
                    <Th className="w-[12%] min-w-[160px] max-w-[220px]">Fornecedor</Th>
                    <Th className="w-[7%] min-w-[90px] max-w-[130px]">Combustível</Th>
                    <Th className="w-[7%] min-w-[90px] max-w-[120px]">Qtde</Th>
                    <Th className="w-[9%] min-w-[110px] max-w-[150px]">Valor total</Th>
                    <Th className="w-[7%] min-w-[90px] max-w-[120px]">NF</Th>
                    <Th className="w-[7%] min-w-[100px] max-w-[130px]">Tipo</Th>
                    <Th className="w-[7%] min-w-[100px] max-w-[130px]">Status</Th>
                    <Th className="w-[5%] min-w-[80px] max-w-[100px]">Boleto</Th>
                    <Th className="w-[5%] min-w-[70px] max-w-[90px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {fuelingsPaginados.length === 0 ? (
                    <Tr>
                      <Td colSpan={12}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <Droplets className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">Nenhum abastecimento encontrado</div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm ? 'Nenhum resultado para a busca atual. Tente outro termo.' : 'Não há registros de abastecimento no sistema.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    fuelingsPaginados.map((fueling) => {
                      return (
                        <Tr key={fueling.id}>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(fueling.fueling_time)}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                <Clock className="h-[13px] w-[13px] text-slate-400" />
                                {formatTime(fueling.fueling_time)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[240px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                  <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={fueling.vehicle?.brand || `Veículo #${fueling.vehicle_id}`}>
                                    {fueling.vehicle?.brand || `Veículo #${fueling.vehicle_id}`}
                                  </span>
                                  <span className="block truncate text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5" title={fueling.vehicle?.license_plate || ''}>
                                    {fueling.vehicle?.license_plate || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[220px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                  <User className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={fueling.employee?.name || `Motorista #${fueling.employee_id}`}>
                                    {fueling.employee?.name || `Motorista #${fueling.employee_id}`}
                                  </span>
                                  {fueling.employee?.cpf && (
                                    <span className="block truncate text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5" title={fueling.employee.cpf}>
                                      {fueling.employee.cpf}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[220px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200">
                                  <Building2 className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={fueling.provider?.name || `Fornecedor #${fueling.provider_id}`}>
                                    {fueling.provider?.name || `Fornecedor #${fueling.provider_id}`}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 rounded-lg bg-purple-50 px-2.5 py-1 ring-1 ring-inset ring-purple-200">
                                <Droplets className="h-3.5 w-3.5 text-purple-600 stroke-[2.1]" />
                                <span className="text-[12px] font-bold text-purple-800 leading-none">
                                  {fueling.fuel_type}
                                </span>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="text-[13px] font-bold text-slate-800 tabular-nums">
                              {fueling.amount}<span className="text-[11px] font-semibold text-slate-500">L</span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <Banknote className="h-[15px] w-[15px] text-emerald-600" />
                              <span className="text-[13px] font-bold text-emerald-700 tabular-nums">
                                {formatCurrency(fueling.total_cost)}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              {fueling.invoice_number ? (
                                <div className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1 ring-1 ring-inset ring-slate-200">
                                  <Receipt className="h-3.5 w-3.5 text-slate-600 stroke-[2.1]" />
                                  <span className="text-[12px] font-bold text-slate-800 leading-none tabular-nums">
                                    {fueling.invoice_number}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[12px] font-semibold text-slate-400">-</span>
                              )}
                            </div>
                          </Td>
                          <Td>{renderTipoBadge(fueling.status)}</Td>
                          <Td>{renderStatusBadge(fueling.status)}</Td>
                          <Td>
                            <div className="flex justify-center">
                              {fueling.boleto_photo?.url ? (
                                <button
                                  onClick={() => handleImageClick(fueling.boleto_photo!.url)}
                                  className="group relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 ring-1 ring-inset ring-slate-200 transition-all hover:ring-amber-300 hover:shadow-md"
                                  title="Ver boleto"
                                >
                                  <div className="absolute top-0.5 left-0.5 z-10 flex h-4 w-4 items-center justify-center rounded bg-amber-600 text-white ring-1 ring-white/80 shadow">
                                    <Receipt className="h-2.5 w-2.5 stroke-[2.2]" />
                                  </div>
                                  <img
                                    src={fueling.boleto_photo.url}
                                    alt="Boleto"
                                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                  />
                                </button>
                              ) : (
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-300">
                                  <ImageIcon className="h-4.5 w-4.5 stroke-[1.8]" />
                                </div>
                              )}
                            </div>
                          </Td>
                          <Td>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewFueling(fueling)}
                                title="Visualizar"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                  {' registro(s)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${n === currentPage ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'}`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {/* Image Preview Modal (Lightbox) */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/75 backdrop-blur-sm p-4 sm:p-6"
          onClick={handleCloseImageModal}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseImageModal}
              className="absolute -top-3 -right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg ring-1 ring-slate-200 transition-all hover:bg-slate-100 hover:text-slate-900 z-10"
              aria-label="Fechar"
            >
              <X className="h-5 w-5 stroke-[2]" />
            </button>
            <div className="overflow-hidden rounded-2xl border border-white/20 shadow-2xl bg-black/20">
              <img
                src={selectedImage}
                alt="Boleto / Visualização completa"
                className="w-full max-h-[85vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {isViewModalOpen && selectedFueling && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-4xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 text-white shadow-[0_6px_16px_-8px_rgba(245,158,11,0.6)]">
                <Droplets className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes do Abastecimento
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Registro #{selectedFueling.id} • {selectedFueling.fuel_type}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-6">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados do Abastecimento</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">ID</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">#{selectedFueling.id}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Tipo de Registro</span>
                    <div className="mt-0.5">{renderTipoBadge(selectedFueling.status)}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Status</span>
                    <div className="mt-0.5">{renderStatusBadge(selectedFueling.status)}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Ação</span>
                    <p className="text-sm text-slate-900 font-medium">{selectedFueling.action || '-'}</p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Data / Hora do abastecimento</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">{formatDateTime(selectedFueling.fueling_time)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Combustível</span>
                    <p className="text-sm text-slate-900 font-semibold inline-flex items-center gap-1.5">
                      <Droplets className="h-4 w-4 text-purple-600 stroke-[2]" />
                      {selectedFueling.fuel_type}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Quantidade</span>
                    <p className="text-sm text-slate-900 font-bold tabular-nums">{selectedFueling.amount} Litros</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Valor Total</span>
                    <p className="text-sm font-bold text-emerald-700 tabular-nums inline-flex items-center gap-1.5">
                      <Banknote className="h-4 w-4 text-emerald-600" />
                      {formatCurrency(selectedFueling.total_cost)}
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Quilometragem</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">{selectedFueling.odometer_reading || '-'}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Nota Fiscal</span>
                    <p className="text-sm text-slate-900 font-semibold tabular-nums">
                      {selectedFueling.invoice_number ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Receipt className="h-4 w-4 text-slate-600 stroke-[2]" />
                          {selectedFueling.invoice_number}
                        </span>
                      ) : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Veículo, Motorista e Fornecedor</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-5 md:grid-cols-3">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                      <Car className="h-5 w-5 stroke-[1.9]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-500 mb-1">Veículo</div>
                      <p className="text-sm text-slate-900 font-semibold leading-tight">
                        {selectedFueling.vehicle?.brand || `Veículo #${selectedFueling.vehicle_id}`}
                      </p>
                      {selectedFueling.vehicle?.license_plate && (
                        <p className="text-[11.5px] font-semibold text-slate-500 mt-0.5 leading-tight">
                          Placa: {selectedFueling.vehicle.license_plate}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                      <User className="h-5 w-5 stroke-[1.9]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-500 mb-1">Motorista</div>
                      <p className="text-sm text-slate-900 font-semibold leading-tight">
                        {selectedFueling.employee?.name || `Motorista #${selectedFueling.employee_id}`}
                      </p>
                      {selectedFueling.employee?.cpf && (
                        <p className="text-[11.5px] font-semibold text-slate-500 mt-0.5 leading-tight">
                          CPF: {selectedFueling.employee.cpf}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200">
                      <Building2 className="h-5 w-5 stroke-[1.9]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-500 mb-1">Fornecedor</div>
                      <p className="text-sm text-slate-900 font-semibold leading-tight">
                        {selectedFueling.provider?.name || `Fornecedor #${selectedFueling.provider_id}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-purple-500 to-purple-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-purple-700">Observações e Detalhes</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-slate-500">Observações</span>
                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap leading-relaxed">
                    {selectedFueling.notes || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-1 pt-3 border-t border-slate-200/70">
                  <span className="text-xs font-semibold text-slate-500">Detalhes da ação</span>
                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap leading-relaxed">
                    {selectedFueling.details || '-'}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-500 to-orange-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-amber-700">Auditoria e Boleto</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Criado em</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">{formatDateTime(selectedFueling.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Atualizado em</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">{formatDateTime(selectedFueling.updated_at)}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 pt-4 border-t border-slate-200/70">
                  <div className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-amber-700">
                    <Receipt className="h-3.5 w-3.5 stroke-[2]" />
                    Boleto do abastecimento
                  </div>
                  {selectedFueling.boleto_photo?.url ? (
                    <button
                      onClick={() => handleImageClick(selectedFueling.boleto_photo!.url)}
                      className="group self-start block max-w-md w-full overflow-hidden rounded-xl border border-slate-200 transition-all hover:ring-2 hover:ring-amber-300"
                    >
                      <img
                        src={selectedFueling.boleto_photo.url}
                        alt="Boleto do abastecimento"
                        className="max-h-96 w-full h-auto object-contain bg-slate-50 transition-transform group-hover:scale-[1.01]"
                      />
                    </button>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-50 py-10 text-slate-400 border border-dashed border-slate-200 max-w-md w-full">
                      <ImageIcon className="h-10 w-10 stroke-[1.8]" />
                      <span className="text-[12.5px] font-semibold">Nenhum boleto anexado.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={(f) => { setAppliedFilters(f); setCurrentPage(1); }}
        filterCategories={filterCategories}
        initialFilters={appliedFilters}
      />
    </>
  );
};

export default HistoricoDeAbastecimento;
